import React from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from "recharts";

interface ChecklistItem {
    month: number;
    year: number;
    complianceCount: number;
    failureCount: number;
    compliancePercentage?: number;
    failurePercentage?: number;
}

interface ChecklistChartProps {
    data?: ChecklistItem[];
}

const ChecklistChart = ({ data = [] }: ChecklistChartProps) => {
    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const chartData = data.map((item) => ({
        ...item,
        monthName: monthNames[item.month - 1],
        compliance: item.complianceCount,
        failure: item.failureCount,
        total: item.complianceCount + item.failureCount,
        percentage: Number(item.compliancePercentage || 0),
    }));

    const totals = chartData.map((item) => item.total);

    const maxValue = Math.max(...totals, 5);

    const CustomTooltip = ({
        active,
        payload,
    }: {
        active?: boolean;
        payload?: any[];
        label?: string;
    }) => {
        if (!active || !payload?.length) {
            return null;
        }

        const item = payload[0]?.payload;

        if (!item) {
            return null;
        }

        const isCompliance = payload[0]?.dataKey === "compliance";

        return (
            <div
                style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "#fff",
                    boxShadow: "0 2px 10px rgba(0,0,0,.15)",
                    minWidth: "180px",
                    fontFamily: "Inter, sans-serif",
                }}
            >
                <div
                    style={{
                        fontWeight: 600,
                        marginBottom: "8px",
                    }}
                >
                    {item.monthName} {item.year}
                </div>

                <div>
                    Total: <b>{item.total}</b>
                </div>

                {isCompliance ? (
                    <>
                        <div
                            style={{
                                color: "var(--primary-color)",
                            }}
                        >
                            Compliance: <b>{item.complianceCount}</b>
                        </div>

                        <div>
                            Percentage:{" "}
                            <b>{item.compliancePercentage}%</b>
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            style={{
                                color: "#BF613B",
                            }}
                        >
                            Failure: <b>{item.failureCount}</b>
                        </div>

                        <div>
                            Percentage:{" "}
                            <b>{item.failurePercentage}%</b>
                        </div>
                    </>
                )}
            </div>
        );
    };

    /**
     * Percentage label inside the compliance section.
     */
    const ComplianceLabel = (props: any) => {
        const { x, y, width, height, value, payload} = props;

        if (!value || height <= 20) {
            return null;
        }
        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#fff"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={14}
                fontWeight="bold"
            >
                {value}%
            </text>
        );
    };

    /**
     * Total label displayed above the complete stacked bar.
     */
    const TotalLabel = (props: any) => {
        const { x, y, width,value} = props;

        return (
            <text
                x={x + width / 2}
                y={y - 8}
                fill="#222"
                textAnchor="middle"
                fontSize={16}
                fontWeight={700}
            >
                {value}
            </text>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={380}>
            <BarChart
                data={chartData}
                margin={{
                    top: 25,
                    right: 10,
                    left: 0,
                    bottom: 5,
                }}
                barCategoryGap="30%"
            >
                <defs>
                    {/* Compliance gradient */}
                    <linearGradient
                        id="complianceGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor="var(--primary-color)"
                        />
                        <stop
                            offset="100%"
                            stopColor="var(--primary-medium-main-color)"
                        />
                    </linearGradient>

                    {/* Failure gradient */}
                    <linearGradient
                        id="failureGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor="#BF613B"
                        />
                        <stop
                            offset="100%"
                            stopColor="#BF613B9A"
                        />
                    </linearGradient>
                </defs>

                <CartesianGrid
                    vertical={false}
                    stroke="var(--primary-medium-main-color)"
                    strokeDasharray="4 4"
                />

                <XAxis
                    dataKey="monthName"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                        fontSize: 16,
                        fontWeight: 900,
                    }}
                />

                <YAxis
                    domain={[0, maxValue]}
                    tickCount={6}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    tick={{
                        fontSize: 14,
                    }}
                />

                <Tooltip
                    cursor={false}
                    content={<CustomTooltip />}
                />

                {/* Compliance */}
                <Bar
                    dataKey="compliance"
                    name="Completed"
                    stackId="checklist"
                    fill="url(#complianceGradient)"
                    radius={[0, 0, 8, 8]}
                >
                    <LabelList
                    dataKey="percentage"
                        content={<ComplianceLabel />}
                    />
                </Bar>

                {/* Failure */}
                <Bar
                    dataKey="failure"
                    name="Failure"
                    stackId="checklist"
                    fill="url(#failureGradient)"
                    radius={[8, 8, 0, 0]}
                >
                    <LabelList
                        dataKey="total"
                        content={<TotalLabel />}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default ChecklistChart;